/**
 * Route configuration — defines which roles can access which routes.
 *
 * `ROUTE_ACCESS` is the single source of roles per route (spec
 * portal-perfis-designador-empresa, Requirement 8): the router guards
 * (`createRoleGuard([...rolesFor(path)])`) and the menu (`ROUTE_CONFIG.allowedRoles`)
 * both derive from it, so a menu item is visible iff its route opens.
 *
 * Order and grouping aligned with prototype (docs/prototype.html NAV_ITEMS).
 * DEVELOPER group kept separate for usability (prototype shows as single SISTEMA item,
 * but implementation has full sub-pages).
 */

import { NEW_PORTAL_ROLES, PORTAL_USER_ROLES } from '@smartsafe/shared';

import type { PortalUserRole } from '../types/portal-role';

/**
 * A single route entry with path, label, icon identifier, and allowed roles.
 */
export interface RouteEntry {
  /** Route path (e.g. '/devices') */
  path: string;
  /** Display label for navigation (pt-BR) */
  label: string;
  /** Icon identifier for the navigation item */
  icon: string;
  /** Roles allowed to access this route (always `rolesFor(path)`) */
  allowedRoles: readonly PortalUserRole[];
  /** Navigation group for sidebar organization */
  group: 'monitor' | 'relatorio' | 'cadastro' | 'system' | 'developer';
  /** Badge count (optional, e.g. alert count) */
  badge?: number;
}

/** Os 6 perfis anteriores à spec portal-perfis-designador-empresa. */
const LEGACY_ROLES: readonly PortalUserRole[] = [
  'platform_admin',
  'operator',
  'client_admin',
  'client_viewer',
  'auditor',
  'developer',
];

const DESIGNADOR_ROLES: readonly PortalUserRole[] = ['designador_admin', 'designador_viewer'];

/**
 * Perfis por rota do router (paths no formato do TanStack Router, `$param`).
 *
 * Perfis antigos: exatamente o guard anterior a esta feature (snapshot em
 * `legacy-guard-snapshot.ts`, Property 3). Perfis novos: Requirement 3.1.
 * Rota sem entrada → nenhum perfil (fail-closed).
 */
export const ROUTE_ACCESS: Readonly<Record<string, readonly PortalUserRole[]>> = {
  // Monitoramento
  '/': PORTAL_USER_ROLES,
  '/sensores': ['platform_admin', 'operator'],
  '/financeiro': ['platform_admin', 'operator', 'client_admin', 'client_viewer', ...NEW_PORTAL_ROLES],
  '/equipamentos': [...LEGACY_ROLES, ...NEW_PORTAL_ROLES],
  '/equipamentos/$deviceId': [...LEGACY_ROLES, ...NEW_PORTAL_ROLES],
  '/alerts': LEGACY_ROLES,
  '/coletas': ['platform_admin', 'operator', 'client_admin', ...DESIGNADOR_ROLES],
  '/map': ['platform_admin', 'operator', 'client_admin'],

  // Relatórios
  '/reports': LEGACY_ROLES,
  '/suporte-tecnico': ['platform_admin', 'operator'],
  '/estoque': ['platform_admin', 'operator'],
  '/help-desk': ['platform_admin', 'operator', 'client_admin', ...NEW_PORTAL_ROLES],

  // Cadastros
  '/admin/designadores': ['platform_admin', 'operator'],
  '/admin/empresas': ['platform_admin', 'operator', ...DESIGNADOR_ROLES],
  '/admin/filiais': ['platform_admin', 'operator', ...NEW_PORTAL_ROLES],
  '/admin/pessoas': ['platform_admin', 'operator', ...NEW_PORTAL_ROLES],
  '/admin/dispositivos': ['platform_admin', 'operator'],
  '/admin/dispositivos/novo': ['platform_admin', 'operator'],
  '/admin/dispositivos/$deviceId/editar': ['platform_admin', 'operator'],
  '/admin/utilizadores': ['platform_admin', 'operator'],
  '/admin/capacidades': ['platform_admin', 'client_admin'],
  '/admin/configuracoes': ['platform_admin'],

  // Sistema
  '/sistema/config': ['platform_admin'],
  '/sistema/ota': ['platform_admin'],
  '/lgpd': ['platform_admin', 'auditor'],
  '/audit': ['platform_admin', 'auditor'],

  // Developer
  '/developer/docs': ['platform_admin', 'developer', 'client_admin'],
  '/developer/keys': ['platform_admin', 'developer', 'client_admin'],
  '/developer/webhooks': ['platform_admin', 'developer', 'client_admin'],
  '/developer/sandbox': ['platform_admin', 'developer'],
  '/developer/events': ['platform_admin', 'developer'],
};

const NO_ROLES: readonly PortalUserRole[] = [];

/**
 * Perfis que abrem a rota. Aceita `:param` (menu) ou `$param` (router).
 * Rota desconhecida → `[]` (fail-closed).
 */
export function rolesFor(path: string): readonly PortalUserRole[] {
  return ROUTE_ACCESS[path.replace(/:(\w+)/g, '$$$1')] ?? NO_ROLES;
}

/**
 * Complete route configuration for the portal.
 *
 * Order follows prototype:
 * MONITORAMENTO → RELATÓRIOS → CADASTROS → SISTEMA → DEVELOPER
 */
export const ROUTE_CONFIG: RouteEntry[] = [
  // ═══════════════════════════════════════════
  // MONITORAMENTO
  // ═══════════════════════════════════════════
  {
    path: '/',
    label: 'Dashboard',
    icon: 'dashboard',
    allowedRoles: rolesFor('/'),
    group: 'monitor',
  },
  {
    path: '/sensores',
    label: 'Monitor Sensores',
    icon: 'sensor',
    allowedRoles: rolesFor('/sensores'),
    group: 'monitor',
  },
  {
    path: '/financeiro',
    label: 'Financeiro',
    icon: 'finance',
    allowedRoles: rolesFor('/financeiro'),
    group: 'monitor',
  },
  {
    path: '/equipamentos',
    label: 'Equipamentos',
    icon: 'device',
    allowedRoles: rolesFor('/equipamentos'),
    group: 'monitor',
  },
  {
    path: '/equipamentos/:deviceId',
    label: 'Equipamento',
    icon: 'device',
    allowedRoles: rolesFor('/equipamentos/:deviceId'),
    group: 'monitor',
  },
  {
    path: '/alerts',
    label: 'Alertas',
    icon: 'alert',
    allowedRoles: rolesFor('/alerts'),
    group: 'monitor',
  },
  {
    path: '/coletas',
    label: 'Coletas',
    icon: 'collection',
    allowedRoles: rolesFor('/coletas'),
    group: 'monitor',
  },

  // ═══════════════════════════════════════════
  // RELATÓRIOS
  // ═══════════════════════════════════════════
  {
    path: '/reports',
    label: 'Relatórios',
    icon: 'reports',
    allowedRoles: rolesFor('/reports'),
    group: 'relatorio',
  },
  // Hidden until AI feature is complete (see handoff.md)
  // {
  //   path: '/ai',
  //   label: 'Inteligência Artificial',
  //   icon: 'ai',
  //   allowedRoles: rolesFor('/ai'),
  //   group: 'relatorio',
  // },
  {
    path: '/suporte-tecnico',
    label: 'Suporte Técnico',
    icon: 'support',
    allowedRoles: rolesFor('/suporte-tecnico'),
    group: 'relatorio',
  },
  {
    path: '/estoque',
    label: 'Estoque / Inventário',
    icon: 'inventory',
    allowedRoles: rolesFor('/estoque'),
    group: 'relatorio',
  },
  {
    path: '/help-desk',
    label: 'Help Desk',
    icon: 'helpdesk',
    allowedRoles: rolesFor('/help-desk'),
    group: 'relatorio',
  },

  // ═══════════════════════════════════════════
  // CADASTROS
  // ═══════════════════════════════════════════
  {
    path: '/admin/designadores',
    label: 'Designadores',
    icon: 'tag',
    allowedRoles: rolesFor('/admin/designadores'),
    group: 'cadastro',
  },
  {
    path: '/admin/empresas',
    label: 'Empresas',
    icon: 'building',
    allowedRoles: rolesFor('/admin/empresas'),
    group: 'cadastro',
  },
  {
    path: '/admin/filiais',
    label: 'Filiais',
    icon: 'branch',
    allowedRoles: rolesFor('/admin/filiais'),
    group: 'cadastro',
  },
  {
    path: '/admin/pessoas',
    label: 'Pessoas',
    icon: 'people',
    allowedRoles: rolesFor('/admin/pessoas'),
    group: 'cadastro',
  },
  // Hidden: replaced by "Permissão de Operadores" (/admin/associar-equipamentos)
  // {
  //   path: '/admin/utilizadores',
  //   label: 'Permissões de Operadores',
  //   icon: 'permissions',
  //   allowedRoles: rolesFor('/admin/utilizadores'),
  //   group: 'cadastro',
  // },
  {
    path: '/admin/capacidades',
    label: 'Capacidades',
    icon: 'capability',
    allowedRoles: rolesFor('/admin/capacidades'),
    group: 'cadastro',
  },
  {
    path: '/admin/configuracoes',
    label: 'Configurações',
    icon: 'settings',
    allowedRoles: rolesFor('/admin/configuracoes'),
    group: 'cadastro',
  },

  // ═══════════════════════════════════════════
  // SISTEMA
  // ═══════════════════════════════════════════
  {
    path: '/sistema/config',
    label: 'Config. Plataforma',
    icon: 'platform',
    allowedRoles: rolesFor('/sistema/config'),
    group: 'system',
  },
  {
    path: '/sistema/ota',
    label: 'OTA Firmware',
    icon: 'ota',
    allowedRoles: rolesFor('/sistema/ota'),
    group: 'system',
  },
  {
    path: '/lgpd',
    label: 'LGPD Compliance',
    icon: 'privacy',
    allowedRoles: rolesFor('/lgpd'),
    group: 'system',
  },
  {
    path: '/audit',
    label: 'Auditoria',
    icon: 'audit',
    allowedRoles: rolesFor('/audit'),
    group: 'system',
  },

  // ═══════════════════════════════════════════
  // DEVELOPER
  // ═══════════════════════════════════════════
  {
    path: '/developer/docs',
    label: 'API Docs',
    icon: 'docs',
    allowedRoles: rolesFor('/developer/docs'),
    group: 'developer',
  },
  {
    path: '/developer/keys',
    label: 'API Keys',
    icon: 'key',
    allowedRoles: rolesFor('/developer/keys'),
    group: 'developer',
  },
  {
    path: '/developer/webhooks',
    label: 'Webhooks',
    icon: 'webhook',
    allowedRoles: rolesFor('/developer/webhooks'),
    group: 'developer',
  },
  {
    path: '/developer/sandbox',
    label: 'Sandbox',
    icon: 'sandbox',
    allowedRoles: rolesFor('/developer/sandbox'),
    group: 'developer',
  },
  {
    path: '/developer/events',
    label: 'Event Log',
    icon: 'log',
    allowedRoles: rolesFor('/developer/events'),
    group: 'developer',
  },
];

/**
 * Returns the allowed roles for a given route path.
 *
 * @param path - The route path to look up
 * @returns Array of allowed roles, or empty array if route not found
 */
export function getAllowedRolesForRoute(path: string): readonly PortalUserRole[] {
  const entry = ROUTE_CONFIG.find((r) => r.path === path);
  return entry?.allowedRoles ?? NO_ROLES;
}

/**
 * Checks if a given role is allowed to access a specific route.
 *
 * @param role - The user's portal role
 * @param path - The route path to check
 * @returns true if the role is allowed, false otherwise
 */
export function isRoleAllowedForRoute(role: PortalUserRole, path: string): boolean {
  const allowedRoles = getAllowedRolesForRoute(path);
  return allowedRoles.includes(role);
}
